import { FormControl, MenuItem, Select } from '@mui/material';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useLanguage } from '../../../contexts/language';
import useForm from '../../../hooks/useForm';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';
import InputHeader from '../../../components/InputHeader';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import { downloadFile } from '../../../utils/files';
import { useEffect } from 'react';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

export interface CsvExportDialogProps {
  allowedOrderedYears: string[];
  isOpen: boolean;
  onClose: () => void;
}

const CsvExportDialog = ({
  allowedOrderedYears,
  isOpen,
  onClose,
}: CsvExportDialogProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const { getYearAnalyticsCsvFile } = useIotsApi();

  const getCsvFileMutation = useMutation({
    mutationFn: (year: number) => {
      return getYearAnalyticsCsvFile(year).then(file => ({ year, file }));
    },
    onSuccess: ({ year, file }) => {
      downloadFile(file, `iots-analytics-${year}.csv`);
      onClose();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.analytics-file-generation-error'),
        ),
      });
      getCsvFileMutation.reset();
    },
  });

  const formState = useForm({
    initialValues: {
      year: '',
    },
    onSubmit: ({ year }) => {
      if (!year) {
        return;
      }
      getCsvFileMutation.mutate(Number(year));
    },
    validate: (value, setError) => {
      if (!value.year) {
        setError('year', t('validation.required-field'));
      }
    },
  });

  const { getBaseProps, getSelectProps, renderError } =
    getMaterialInputsFormHelper(formState, {
      shouldInputsBeDisabled: getCsvFileMutation.isPending,
    });

  useEffect(() => {
    if (!isOpen && formState.haveValuesChanged) {
      formState.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <ConfirmationDialog
      title={t('content.export-to-csv')}
      onConfirm={formState.handleSubmit}
      onClose={onClose}
      loading={getCsvFileMutation.isPending}
      open={isOpen}
      confirmButtonText={t('content.export-to-csv')}
      submitDisabled={formState.hasVisibleErrors}
    >
      <p className="mb-4">{t('content.analytics-csv-export-description')}</p>
      <FormControl error={getBaseProps('year').error}>
        <InputHeader required>{t('content.year')}</InputHeader>
        <Select<string>
          {...getSelectProps('year')}
          displayEmpty
          renderValue={value => value || t('content.select')}
        >
          {allowedOrderedYears.map(year => (
            <MenuItem key={year} value={year.toString()}>
              {year}
            </MenuItem>
          ))}
        </Select>
        {renderError('year')}
      </FormControl>
    </ConfirmationDialog>
  );
};

export default CsvExportDialog;
